import { useMemo } from 'react';
import { t } from '@lingui/macro';

import Panel from '../../page-framework/panel/PanelDecorator';
import {
    PANEL_CATEGORIES,
    LINE_CHART_PANEL_PREVIEW_IMAGE,
    PANEL_PRIORITIES,
} from '../../page-framework/panel/PanelRegistry';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import usePanelDataService from '../../../services/PanelDataService';
import PerformanceChart from '../../charts/performance-chart/PerformanceChart';
import { BuiltInTooltip } from '../../charts/tooltip/types';
import { useMetricColor } from '../../../utilities/ColorUtilities';
import { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import { radioFilterId, timeRangeFilterId } from '../../filters';

const title = t`Radio Retry Rate`;

const dataSource = {
    url: '/query/chart',
    params: {
        type: 'radio',
        metrics: ['retryRatePerc', 'retryStatus'],
    },
};

export function useChartConfig() {
    const color = useMetricColor('serverCpu');

    return useMemo(
        () => ({
            yAxis: [
                {
                    title: {
                        text: t`Retry Rate (%)`,
                    },
                    ceiling: 100,
                },
            ],
            tooltip: BuiltInTooltip,
            showGranularity: true,
            statusMetric: 'retryStatus',
            chartTrends: [
                {
                    formatter: FORMATTER_TYPES.PERCENT,
                    forceDecimals: true,
                    units: '%',
                    chartType: 'spline',
                    key: 'retryRatePerc',
                    label: t`Retry Rate`,
                    color,
                    multiplier: 1,
                },
            ],
        }),
        [color]
    );
}

function RadioRetryRatePanel() {
    const chartConfig = useChartConfig();
    const data = usePanelDataService(dataSource);
    const chartData = data?.data?.[0];

    if (!data) return null;

    return <PerformanceChart chartDef={chartConfig} data={chartData} />;
}

RadioRetryRatePanel.propTypes = panelPropTypes;

RadioRetryRatePanel.defaultProps = {
    ...panelPropDefaults,
    title,
    contentHeight: 175,
    minContentHeight: 175,
};

export default Panel({
    typeAlias: 'RadioRetryRateTrend',
    catalogEntry: {
        category: PANEL_CATEGORIES.RADIO,
        priority: PANEL_PRIORITIES.CHART,
        title,
        description: t`Retry rate trend for a selected radio`,
        previewImage: LINE_CHART_PANEL_PREVIEW_IMAGE,
        ipm: true,
    },
    filters: {
        required: [timeRangeFilterId, radioFilterId],
        optional: [],
    },
})(RadioRetryRatePanel);
